import { ACTIVE_CUSTOM_THEME_KEY, isDefaultThemeActive, theme, } from "../../theme";
/**
 * Modifies SVG colors to match the application's theme colors. Needed because
 * the SVGs are static assets and not created with the theme in mind.
 *
 * This function solves the problem of maintaining consistent branding across SVG icons
 * by dynamically replacing specific colors with theme-based colors. It's particularly useful
 * when SVGs need to adapt to theme changes or custom branding requirements.
 *
 * The function:
 * 1. Fetches the SVG content from the provided URL
 * 2. Parses it as an XML document
 * 3. Finds all elements with 'fill' attributes
 * 4. Replaces specific color codes with theme colors:
 *    - #9CD3FF → theme.color.fillInfo
 *    - #203C63 → theme.color.text (or darkBlue100 as fallback)
 *
 * @param url - The URL of the SVG file to modify
 * @returns Promise<string> - A promise that resolves to the modified SVG content as a string
 */
export const modifySvgColors = async (url) => {
    try {
        const response = await fetch(url);
        const svgText = await response.text();
        // Create a DOM parser to work with the SVG
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgText, "image/svg+xml");
        // Find all elements with fill attribute
        const elementsWithFill = svgDoc.querySelectorAll("[fill]");
        elementsWithFill.forEach(element => {
            const currentFill = element.getAttribute("fill");
            const isDefaultTheme = theme.color.primary.toUpperCase() === "#2BB7D9";
            // Replace specific colors (add more as needed)
            if (currentFill.toUpperCase() === "#9CD3FF" && !isDefaultTheme) {
                element.setAttribute("fill", theme.color.primary);
            }
            else if (currentFill.toUpperCase() === "#203C63" && !isDefaultTheme) {
                element.setAttribute("fill", theme.color.fillInfo);
            }
        });
        // Convert back to string
        const serializer = new XMLSerializer();
        return serializer.serializeToString(svgDoc);
    }
    catch (error) {
        return url;
    }
};
/**
 * Retrieves the custom branding logo URL from localStorage, falling back to default if not found
 * @param defaultLogoUrl - The fallback logo URL to use if no custom logo is set
 * @returns The logo URL to display
 */
export const getCustomBrandingLogo = (defaultLogoUrl) => {
    try {
        // Check if we have any custom branding stored
        if (isDefaultThemeActive())
            return defaultLogoUrl;
        // Parse the stored theme JSON and extract the logo URL
        const parsedTheme = JSON.parse(localStorage.getItem(ACTIVE_CUSTOM_THEME_KEY));
        return parsedTheme?.logo || defaultLogoUrl;
    }
    catch (error) {
        // If anything fails (invalid JSON, localStorage unavailable, etc), use default
        return defaultLogoUrl;
    }
};
